import { TuneOutlined } from '@mui/icons-material';
import { Alert, Box, Divider, MenuItem, Stack, Typography } from '@mui/material';
import { ChoiceControl, FeedbackSnackbar, FormTextField, HelpPopover, SectionCard, designTokens, ValueBadge } from '../../../design-system';
import { previewParts } from '../model/employeeNumber';
import type { EmployeeNumberSettings } from '../model/types';
import { EmployeeNumberPreview } from './EmployeeNumberPreview';
export function EmployeeNumberEditor({
  draft,
  canWrite,
  saving,
  error,
  notice,
  onNoticeClose,
  onChange,
}: {
  draft: EmployeeNumberSettings;
  canWrite: boolean;
  saving: boolean;
  error?: string;
  notice?: string;
  onNoticeClose: () => void;
  onChange: (settings: EmployeeNumberSettings) => void;
}) {
  const preview = previewParts(draft);
  const prefixValid = /^[A-Za-z0-9._-]{0,16}$/.test(draft.prefix);
  const set = <K extends keyof EmployeeNumberSettings>(field: K, value: EmployeeNumberSettings[K]) =>
    onChange({
      ...draft,
      [field]: value,
    });
  const changeDateFormat = (dateFormat: EmployeeNumberSettings['dateFormat']) =>
    onChange({
      ...draft,
      dateFormat,
      resetCycle:
        dateFormat === 'NONE'
          ? 'NEVER'
          : draft.resetCycle === 'MONTHLY' && dateFormat === 'YY'
            ? 'YEARLY'
            : draft.resetCycle,
    });
  return (
    <Stack spacing={1.5}>
      {!canWrite ? <Alert severity="warning">이 설정을 조회할 수 있지만 변경 권한은 없습니다.</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={onNoticeClose} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(0, 1.65fr) minmax(300px, 0.75fr)',
          },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <SectionCard
          help="이 규칙은 새 입사 초안을 만들 때 적용됩니다. 이미 발급했거나 승인 대기 중인 사번은 바뀌지 않습니다."
          bodyPadding="standard"
          title={
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <TuneOutlined color="action" fontSize="small" />
              <span>사번 규칙</span>
            </Stack>
          }
        >
          <Stack spacing={2.25}>
            <Stack spacing={1.25}>
              <Typography component="h3" sx={designTokens.typography.subsectionTitle}>번호 형식</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                  },
                  gap: 1.5,
                }}
              >
                <FormTextField
                  label="접두어"
                  value={draft.prefix}
                  disabled={!canWrite || saving}
                  error={!prefixValid}
                  onChange={(event) => set('prefix', event.target.value)}
                  helperText={
                    prefixValid
                      ? '영문, 숫자, 점, 밑줄, 하이픈을 16자까지 사용할 수 있습니다.'
                      : '허용되지 않는 문자이거나 16자를 넘었습니다.'
                  }
                  slotProps={{
                    htmlInput: {
                      maxLength: 16,
                    },
                  }}
                />
                <FormTextField
                  select
                  label="날짜 기준"
                  value={draft.dateBasis}
                  disabled={!canWrite || saving}
                  onChange={(event) => set('dateBasis', event.target.value as EmployeeNumberSettings['dateBasis'])}
                  help={
                    draft.dateBasis === 'HIRE_DATE'
                      ? '입사 효력일의 날짜를 사용합니다.'
                      : '사번을 발급하는 처리일(UTC)을 사용합니다.'
                  }
                >
                  <MenuItem value="HIRE_DATE"><ValueBadge label="입사일" /></MenuItem>
                  <MenuItem value="GENERATED_DATE"><ValueBadge label="발급 처리일" /></MenuItem>
                </FormTextField>
                <FormTextField
                  select
                  label="날짜 형식"
                  value={draft.dateFormat}
                  disabled={!canWrite || saving}
                  onChange={(event) => changeDateFormat(event.target.value as EmployeeNumberSettings['dateFormat'])}
                  help="YY는 연도 2자리, MM은 월 2자리입니다."
                >
                  <MenuItem value="NONE"><ValueBadge label="날짜 없음" /></MenuItem>
                  <MenuItem value="YY"><ValueBadge label="YY" /></MenuItem>
                  <MenuItem value="YYMM"><ValueBadge label="YYMM" /></MenuItem>
                  <MenuItem value="YYYYMM"><ValueBadge label="YYYYMM" /></MenuItem>
                </FormTextField>
              </Box>
            </Stack>
            <Divider />
            <Stack spacing={1.25}>
              <Typography component="h3" sx={designTokens.typography.subsectionTitle}>순번 운영</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                  },
                  gap: 1.5,
                }}
              >
                <FormTextField
                  select
                  label="순번 자릿수"
                  value={draft.sequenceLength}
                  disabled={!canWrite || saving}
                  onChange={(event) => set('sequenceLength', Number(event.target.value))}
                  help="부족한 앞자리는 0으로 채웁니다."
                >
                  {Array.from(
                    {
                      length: 9,
                    },
                    (_, index) => index + 1,
                  ).map((length) => (
                    <MenuItem key={length} value={length}>
                      <ValueBadge label={`${length}자리`} />
                    </MenuItem>
                  ))}
                </FormTextField>
                <FormTextField
                  select
                  label="순번 초기화"
                  value={draft.resetCycle}
                  disabled={!canWrite || saving}
                  onChange={(event) => set('resetCycle', event.target.value as EmployeeNumberSettings['resetCycle'])}
                  help="같은 범위 안에서 순번을 언제 다시 1부터 시작할지 정합니다."
                >
                  <MenuItem value="NEVER"><ValueBadge label="초기화하지 않음" /></MenuItem>
                  <MenuItem value="YEARLY" disabled={draft.dateFormat === 'NONE'}><ValueBadge label="매년" /></MenuItem>
                  <MenuItem value="MONTHLY" disabled={draft.dateFormat === 'NONE' || draft.dateFormat === 'YY'}><ValueBadge label="매월" /></MenuItem>
                </FormTextField>
                <FormTextField
                  select
                  label="순번 범위"
                  value={draft.sequenceScope}
                  disabled={!canWrite || saving}
                  onChange={(event) =>
                    set('sequenceScope', event.target.value as EmployeeNumberSettings['sequenceScope'])
                  }
                  help={
                    draft.sequenceScope === 'COMPANY'
                      ? '회사마다 독립된 순번을 사용합니다.'
                      : '테넌트의 모든 회사가 하나의 순번을 공유합니다.'
                  }
                >
                  <MenuItem value="COMPANY"><ValueBadge label="회사별" /></MenuItem>
                  <MenuItem value="TENANT"><ValueBadge label="테넌트 전체" /></MenuItem>
                </FormTextField>
              </Box>
            </Stack>
            <Divider />
            <Stack spacing={1.25}>
              <Stack
                data-testid="reuse-former-worker-id-setting"
                direction="row"
                spacing={{
                  xs: 1.5,
                  sm: 2,
                }}
                sx={{
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Typography id="reuse-former-worker-id-label" component="h3" sx={designTokens.typography.subsectionTitle}>
                      퇴직자 사번 재사용
                    </Typography>
                    <HelpPopover title="퇴직자 사번 재사용">동일 인물이 같은 회사에 직원으로 재입사하면 기존 Worker ID와 사번을 유지하고 새 재직 구간을 만듭니다. 새 순번은 소비하지 않습니다.</HelpPopover>
                  </Stack>
                </Box>
                <ChoiceControl
                  kind="switch"
                  ariaLabelledBy="reuse-former-worker-id-label"
                  checked={draft.reuseFormerWorkerId}
                  disabled={!canWrite || saving}
                  onCheckedChange={(checked) => set('reuseFormerWorkerId', checked)}
                />
              </Stack>
              {draft.reuseFormerWorkerId ? (
                <Alert severity="warning">
                  활성화 전 자동 점검 대상인 Rewards 계약·보상·복리후생과 미완료 변경 요청, 휴가 entitlement·잔액을
                  정리해야 합니다. 미해결 항목은 재입사를 REVIEW_REQUIRED로 중단합니다. Worker 문서 접근 범위는 별도
                  운영 검토·승인하고, 기존 역할·그룹 권한과 세션·외부 계정은 별도 재승인·회수 증적을 확인해야 합니다.
                </Alert>
              ) : null}
            </Stack>
          </Stack>
        </SectionCard>
        <EmployeeNumberPreview preview={preview} />
      </Box>
    </Stack>
  );
}
